// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import Zone from "@insite/client-framework/Components/Zone";
import {
    ExcludeFromNavigation,
    ExcludeFromSignInRequired,
    HideFooter,
    HideFromSearchEngines,
    HideFromSiteSearch,
    HideHeader,
    HorizontalRule,
    MetaDescription,
    MetaKeywords,
    OpenGraphImage,
    OpenGraphTitle,
    OpenGraphUrl,
    StructuredPageData,
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Page from "@insite/mobius/Page";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import useParseQueryString from "@store/Framework/Common/Hooks/useParseQueryString";
import { GetTrailerTrackingsV2ApiParameter } from "@store/Framework/Services/TrailerServiceV2";
import clearParameter from "@store/Framework/Store/Greif/Pages/TrailerTrackingV2/Handlers/ClearParameter";
import loadTrailerTrackings from "@store/Framework/Store/Greif/Pages/TrailerTrackingV2/Handlers/LoadTrailerTrackings";
import setParameter from "@store/Framework/Store/Greif/Pages/TrailerTrackingV2/Handlers/SetParameter";
import {
    useTrailerTrackingV2DataView,
    useTrailerTrackingV2State,
} from "@store/Framework/Store/Greif/Pages/TrailerTrackingV2/TrailerTrackingV2Hooks";
import { TrailerTrackingV2DwellTimeOperatorApiValues } from "@store/Framework/Types/ApiModels";
import useIsFirstRender from "@toolkit/Framework/Common/Hooks/useIsFirstRender";
import omit from "lodash/omit";
import pick from "lodash/pick";
import qs from "qs";
import React, { useEffect, useRef } from "react";
import { FC } from "react";

const dwellTimeOperatorValues = Object.values<string>(TrailerTrackingV2DwellTimeOperatorApiValues);

interface QueryStringParameter extends GetTrailerTrackingsV2ApiParameter {
    allLocations?: boolean;
}

const allowedQueryStringParameters: Array<keyof QueryStringParameter> = [
    "alertsOnly",
    "billingType",
    "customerSequence",
    "dwellTimeDays",
    "dwellTimeOperator",
    "page",
    "pageSize",
    "plantCode",
    "poNumber",
    "trailerId",
    // Mapped parameters
    "allLocations", // onSiteOnly
];

export interface TrailerTrackingPageProps extends PageProps {}

const TrailerTrackingPage: FC<TrailerTrackingPageProps> = ({ id }) => {
    const dispatch = useAppDispatch();
    const isFirstRender = useIsFirstRender();
    const history = useHistory();
    const queryString = useParseQueryString<QueryStringParameter>({
        booleans: ["alertsOnly", "allLocations"],
        numbers: [["dwellTimeDays", 0]],
        parsers: {
            dwellTimeOperator: x => (dwellTimeOperatorValues.includes(x) ? x : undefined),
        },
    });
    const { parameter } = useTrailerTrackingV2State();
    const skipQueryStringUpdate = useRef(false);
    const skipLoad = useRef(false);
    const { isLoading, value: results, loadingFailed } = useTrailerTrackingV2DataView();

    useEffect(() => {
        const queryStringParameter = pick(queryString, allowedQueryStringParameters);

        if (Object.keys(queryStringParameter).length) {
            skipQueryStringUpdate.current = true;
            skipLoad.current = true;

            if (typeof queryStringParameter.allLocations !== "undefined") {
                queryStringParameter.onSiteOnly = !queryStringParameter.allLocations;
                delete queryStringParameter.allLocations;
            }

            dispatch(setParameter(queryStringParameter));
        }

        return () => {
            dispatch(clearParameter());
        };
    }, []);

    useEffect(() => {
        if (isFirstRender && skipQueryStringUpdate.current) {
            return;
        }

        if (skipQueryStringUpdate.current) {
            skipQueryStringUpdate.current = false;

            return;
        }

        const _parameter = { ...parameter } as QueryStringParameter;
        if (!_parameter.onSiteOnly) {
            _parameter.allLocations = !_parameter.onSiteOnly;
            delete _parameter.onSiteOnly;
        }

        const newQueryString = qs.stringify({
            ...pick(_parameter, allowedQueryStringParameters),
            ...omit(queryString, allowedQueryStringParameters),
        });

        history.replace(`${window.location.pathname}${newQueryString ? `?${newQueryString}` : ""}`);
    }, [parameter]);

    useEffect(() => {
        if (skipLoad.current) {
            skipLoad.current = false;

            return;
        }

        if (!isLoading && !loadingFailed && !results) {
            dispatch(loadTrailerTrackings(parameter));
        }
    }, [parameter]);

    return (
        <Page>
            <Zone contentId={id} zoneName="Content" />
        </Page>
    );
};

const pageModule: PageModule = {
    component: TrailerTrackingPage,
    definition: {
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        pageType: "System",
        fieldDefinitions: [
            MetaKeywords,
            MetaDescription,
            OpenGraphTitle,
            OpenGraphUrl,
            OpenGraphImage,
            StructuredPageData,
            {
                ...HorizontalRule,
                sortOrder: 199,
            },
            HideHeader,
            HideFooter,
            HideFromSearchEngines,
            HideFromSiteSearch,
            ExcludeFromNavigation,
            ExcludeFromSignInRequired,
        ],
    },
};

export default pageModule;
